import { Box, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import type { ReactNode } from 'react';

export type TableColumn<RowType extends object> = {
  key: string;
  header: string;
  width?: number | string;
  align?: 'left' | 'center' | 'right';
  field?: keyof RowType & string;
  format?: (value: unknown, row: RowType) => ReactNode;
  render?: (row: RowType) => ReactNode;
  monospace?: boolean;
};

type Dimension = number | string | undefined;

export type ReadOnlyTableProps<RowType extends object> = {
  columns: Array<TableColumn<RowType>>;
  rows: RowType[];
  getRowId?: (row: RowType) => string;
  size?: 'sm' | 'md';
  height?: Dimension; // if provided, makes body scrollable horizontally; vertical grows naturally
  className?: string;
  tableLayout?: 'fixed' | 'auto';
};

export function ReadOnlyTable<RowType extends object>({
  columns,
  rows,
  getRowId,
  size = 'sm',
  height,
  className,
  tableLayout = 'fixed',
}: ReadOnlyTableProps<RowType>) {
  const containerStyle: { width: string; height?: string } = { width: '100%' };
  if (typeof height === 'number') {
    containerStyle.height = `${height}px`;
  } else if (typeof height === 'string') {
    containerStyle.height = height;
  }

  const verticalOverflow = height ? 'auto' : undefined;

  return (
    <Box className={className} width="100%">
      <TableContainer
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        overflowX="auto"
        overflowY={verticalOverflow}
        style={containerStyle}
      >
        <Table size={size} variant="simple" width="max-content" minW="100%" style={{ tableLayout }}>
          <colgroup>
            {columns.map(col => (
              <col key={col.key} style={{ width: typeof col.width === 'number' ? `${col.width}px` : col.width }} />
            ))}
          </colgroup>
          <Thead>
            <Tr>
              {columns.map(col => (
                <Th
                  key={col.key}
                  textAlign={col.align ?? 'left'}
                  style={{ width: typeof col.width === 'number' ? `${col.width}px` : col.width }}
                  bg="gray.100"
                  textTransform="none"
                >
                  {col.header}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, rowIndex) => {
              const rowKey = getRowId ? getRowId(row) : String(rowIndex);
              return (
                <Tr key={rowKey}>
                  {columns.map(col => {
                    const widthStyle = typeof col.width === 'number' ? `${col.width}px` : col.width;
                    let value: ReactNode | null = null;
                    if (col.render) {
                      value = col.render(row);
                    } else if (col.field) {
                      const fieldKey = col.field;
                      const cellValue = row[fieldKey];
                      value = col.format ? col.format(cellValue, row) : ((cellValue ?? null) as ReactNode);
                    }
                    return (
                      <Td
                        key={`${rowKey}-${col.key}`}
                        textAlign={col.align ?? 'left'}
                        fontFamily={col.monospace ? 'mono' : undefined}
                        style={{ width: widthStyle, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                      >
                        {value}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}
